import React, { useMemo } from 'react';
import { translate } from '../../Services/translation';
import { MeasuredWallpaperData } from '../../Types/ProductPageModel';
import Icon from '../Icon';
import clsx from 'clsx';

interface MeasuredWallpaperCalculatorProps {
  setCalculatorWidth: (width: number) => void;
  setCalculatorHeight: (height: number) => void;
  calculatorWidth: number;
  calculatorHeight: number;
  calculatorActive: boolean;
  calculatedSizeTooSmall: boolean;
  measuredWallpaper: MeasuredWallpaperData;
  disableWallSizeSelection?: boolean;
}

export const isValidHeight = (
  measuredWallpaper: MeasuredWallpaperData,
  height: number
) => measuredWallpaper.wallHeights.indexOf(height) != -1;

export const isValidWidth = (
  measuredWallpaper: MeasuredWallpaperData,
  width: number
) => measuredWallpaper.wallWidths.indexOf(width) != -1;

const MeasuredWallpaperCalculator = ({
  setCalculatorWidth,
  setCalculatorHeight,
  calculatorWidth,
  calculatorHeight,
  calculatedSizeTooSmall,
  calculatorActive,
  measuredWallpaper,
  disableWallSizeSelection,
}: MeasuredWallpaperCalculatorProps) => {
  const heightOptions = useMemo(
    () =>
      measuredWallpaper.wallHeights.map((height) => ({
        label:
          height.toString() + ' ' + measuredWallpaper.unitOfMeasurement,
        value: height,
      })),
    [measuredWallpaper]
  );

  const widthOptions = useMemo(
    () =>
      measuredWallpaper.wallWidths.map((width) => ({
        label:
          width.toString() + ' ' + measuredWallpaper.unitOfMeasurement,
        value: width,
      })),
    [measuredWallpaper.unitOfMeasurement, measuredWallpaper.wallWidths]
  );

  const handleHeightSelect: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    setCalculatorHeight(parseInt(e.target.value));
  };
  const handleWidthSelect: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    setCalculatorWidth(parseInt(e.target.value));
  };

  return (
    <div className="mb-4">
      <div className="grid grid-cols-2 gap-6">
        <div className="relative">
          <label
            htmlFor="set-width"
            className="absolute !left-2 -top-3 z-10 flex -translate-y-px transform !bg-white px-2 py-1 text-sm text-gray-700"
          >
            {translate('calculator.width')} (
            {measuredWallpaper.unitOfMeasurement})
          </label>
          <select
            id="select-width"
            className={clsx(
              'w-full appearance-none rounded border border-solid border-gray-300 bg-transparent py-4 pl-12 pr-5 text-base font-normal leading-normal transition focus:outline-none',
              disableWallSizeSelection ? 'cursor-auto' : 'cursor-pointer'
            )}
            value={calculatorWidth}
            onChange={handleWidthSelect}
            disabled={disableWallSizeSelection}
          >
            <option value="">
              0 {measuredWallpaper.unitOfMeasurement}
            </option>
            {widthOptions.map((widthOption) => (
              <option
                key={'height' + widthOption.value}
                value={widthOption.value}
              >
                {widthOption.label}
              </option>
            ))}
          </select>
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2"
            name="arrows-horizontal"
          />
        </div>
        <div className="relative">
          <label
            htmlFor="set-height"
            className="absolute !left-2 -top-3 z-10 flex -translate-y-px transform !bg-white px-2 py-1 text-sm text-gray-700"
          >
            {translate('calculator.height')} (
            {measuredWallpaper.unitOfMeasurement})
          </label>
          <select
            id="select-height"
            className={clsx(
              'w-full appearance-none rounded border border-solid border-gray-300 bg-transparent py-4 pl-12 pr-5 text-base font-normal leading-normal transition focus:outline-none',
              disableWallSizeSelection ? 'cursor-auto' : 'cursor-pointer'
            )}
            value={calculatorHeight}
            onChange={handleHeightSelect}
            disabled={disableWallSizeSelection}
          >
            <option value="">
              0 {measuredWallpaper.unitOfMeasurement}
            </option>
            {heightOptions.map((heightOption) => (
              <option
                key={'height' + heightOption.value}
                value={heightOption.value}
              >
                {heightOption.label}
              </option>
            ))}
          </select>
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2"
            name="arrows-vertical"
          />
        </div>
      </div>
      {calculatedSizeTooSmall && calculatorActive && (
        <div className="bold mt-4 flex w-full items-center justify-center rounded border border-error-300 bg-error-100 p-3 text-center text-md">
          {translate('mural-calculator.to-small-size')}
        </div>
      )}
    </div>
  );
};

export default MeasuredWallpaperCalculator;
